import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Globe, ZoomIn, ZoomOut, Minimize2, Maximize2 } from 'lucide-react';
import articleTranslations from '../data/articleTranslations.json';
import ferrari599Translations from './ferrari599Translations.json';
import { PageFlip } from 'page-flip';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const IMG = '/articles/porsche-935';

const FOCAL = {
  hero: 'center 60%',
  detail: 'center right',
  rearLogo: '46% center',
  rear34: 'center 32%',
  engine: 'center center',
  wheel: 'center center',
  interior: '65% center',
  closing: 'center 50%',
};

/* Reference canvas for proportional mobile/tablet scaling */
const DESIGN_W = 1440;
const DESIGN_H = 900;

/* Mobile renders at a smaller canvas for better text readability */
const MOBILE_CANVAS_W = 1080;
const MOBILE_CANVAS_H = 675;

/* ═══════════════════════════════════════════
   BOOK ENGINE — Clean page-flip integration
   Library-native touch for real-time finger curl.
   disableFlipByClick: true prevents tap-to-flip.
   ═══════════════════════════════════════════ */
const BookEngineInner = React.forwardRef(({ width, height, contentScale, pages, onFlip }, ref) => {
  const containerRef = useRef(null);
  const pfRef = useRef(null);
  React.useImperativeHandle(ref, () => ({
    pageFlip: () => pfRef.current,

    /* ── BUTTON-TRIGGERED PAGE TURN (forward) ──
       Drives the library's own flip physics via programmatic position updates.
       Uses startUserTouch → userMove → userStop: same code path as finger drag.
       ONE rendering system — the library draws every frame. */
    curlNext: () => {
      const pf = pfRef.current;
      if (!pf || pf._curlAnimating) return;
      try { if (pf.getState() !== 'read') return; } catch (_) {}
      if (pf.getCurrentPageIndex() >= pf.getPageCount() - 1) return;

      const rect = pf.getRender().getRect();
      const pw = rect.pageWidth;
      const ph = rect.height;
      pf._curlAnimating = true;

      /* Positions relative to .stf__block (distElement).
         Start near right edge → library detects FORWARD direction. */
      const sx = pw - 10;
      const sy = ph * 0.55;
      const ex = -30;

      pf.startUserTouch({ x: sx, y: sy });

      const DUR = 420;
      const t0 = performance.now();
      const animate = (now) => {
        const t = Math.min(1, (now - t0) / DUR);
        const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        pf.userMove({ x: sx + (ex - sx) * ease, y: sy }, false);
        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          pf.userStop({ x: ex, y: sy });
          setTimeout(() => { pf._curlAnimating = false; }, 600);
        }
      };
      requestAnimationFrame(animate);
    },

    /* ── BUTTON-TRIGGERED PAGE TURN (backward) ── */
    curlPrev: () => {
      const pf = pfRef.current;
      if (!pf || pf._curlAnimating) return;
      try { if (pf.getState() !== 'read') return; } catch (_) {}
      if (pf.getCurrentPageIndex() <= 0) return;

      const rect = pf.getRender().getRect();
      const pw = rect.pageWidth;
      const ph = rect.height;
      pf._curlAnimating = true;

      /* Start near left edge → library detects BACKWARD direction. */
      const sx = 10;
      const sy = ph * 0.55;
      const ex = pw + 30;

      pf.startUserTouch({ x: sx, y: sy });

      const DUR = 420;
      const t0 = performance.now();
      const animate = (now) => {
        const t = Math.min(1, (now - t0) / DUR);
        const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        pf.userMove({ x: sx + (ex - sx) * ease, y: sy }, false);
        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          pf.userStop({ x: ex, y: sy });
          setTimeout(() => { pf._curlAnimating = false; }, 600);
        }
      };
      requestAnimationFrame(animate);
    },
    resize: (newW, newH, nativeLayout) => {
      const pf = pfRef.current;
      const container = containerRef.current;
      if (!pf || !container) return;
      const settings = pf.getSettings();
      settings.width = newW;
      settings.height = newH;
      settings.minWidth = newW;
      settings.maxWidth = newW;
      settings.minHeight = newH;
      settings.maxHeight = newH;
      container.style.minWidth = newW + 'px';
      container.style.maxWidth = (newW * 2) + 'px';
      container.style.height = newH + 'px';
      container.style.minHeight = newH + 'px';
      const wrapper = container.querySelector('.stf__wrapper');
      if (wrapper) wrapper.style.paddingBottom = (newH / newW * 100) + '%';
      const items = container.querySelectorAll('[data-page-item] > div');
      if (nativeLayout) {
        /* LANDSCAPE FULL-VIEW: no CSS transform — content sized to container.
           Engine operates in native interaction space. No coordinate remapping. */
        items.forEach(item => {
          item.style.width = newW + 'px';
          item.style.height = newH + 'px';
          item.style.transform = 'none';
          item.style.setProperty('--page-w', newW + 'px');
          item.style.setProperty('--page-h', newH + 'px');
          item.style.setProperty('--vw', (newW / 100) + 'px');
          item.style.setProperty('--vh', (newH / 100) + 'px');
        });
      } else {
        /* ALL OTHER MODES: content at fixed canvas size, CSS-scaled down */
        const newScale = newW / MOBILE_CANVAS_W;
        items.forEach(item => {
          item.style.width = MOBILE_CANVAS_W + 'px';
          item.style.height = MOBILE_CANVAS_H + 'px';
          item.style.transform = `scale(${newScale})`;
          item.style.setProperty('--page-w', MOBILE_CANVAS_W + 'px');
          item.style.setProperty('--page-h', MOBILE_CANVAS_H + 'px');
          item.style.setProperty('--vw', (MOBILE_CANVAS_W / 100) + 'px');
          item.style.setProperty('--vh', (MOBILE_CANVAS_H / 100) + 'px');
        });
      }
      pf.update();
    },
  }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!containerRef.current || pfRef.current) return;

    const pageEls = Array.from(containerRef.current.querySelectorAll('[data-page-item]'));
    if (pageEls.length === 0) return;

    const pf = new PageFlip(containerRef.current, {
      width,
      height,
      size: 'fixed',
      showCover: false,
      usePortrait: true,
      flippingTime: 600,
      maxShadowOpacity: 0.35,
      drawShadow: true,
      mobileScrollSupport: false,
      swipeDistance: 30,
      disableFlipByClick: false,
      useMouseEvents: true,
      startPage: 0,
      clickEventForward: false,
    });

    pf.loadFromHTML(pageEls);
    pf.on('flip', (e) => onFlip(e.data));

    /* Reduce library swipeTimeout for responsive finger-tracking */
    try { if (pf.ui) pf.ui.swipeTimeout = 50; } catch (_) {}

    /* ── Fix library's portrait backward coordinate bug ──
       In portrait mode, convertToPage for backward (direction=1) produces
       coordinates off by one pageWidth, causing FlipCalculation to get
       degenerate values. We monkey-patch convertToPage/convertToGlobal so
       the backward drag uses the exact same native fold physics as forward. */
    pf._curlAnimating = false;
    try {
      const render = pf.getRender();
      const origConvertToPage = render.convertToPage.bind(render);
      const origConvertToGlobal = render.convertToGlobal.bind(render);

      render.convertToPage = function(point, dir) {
        const result = origConvertToPage(point, dir);
        const d = dir !== undefined ? dir : this.direction;
        if (d === 1 && this.getOrientation() === 'portrait') {
          result.x += this.getRect().pageWidth;
        }
        return result;
      };

      render.convertToGlobal = function(point, dir) {
        if (point == null) return null;
        const d = dir !== undefined ? dir : this.direction;
        if (d === 1 && this.getOrientation() === 'portrait') {
          const pw = this.getRect().pageWidth;
          return origConvertToGlobal({ x: point.x - pw, y: point.y }, dir);
        }
        return origConvertToGlobal(point, dir);
      };
    } catch (_) {}

    pfRef.current = pf;
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width, height, boxShadow: '0 4px 40px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.25), inset 0 0 0 0.5px rgba(255,255,255,0.04)' }}
      data-testid="catalogue-canvas"
    >
      {pages.map((page, i) => (
        <div key={i} data-page-item style={{ overflow: 'hidden' }}>
          <div style={{
            width: MOBILE_CANVAS_W,
            height: MOBILE_CANVAS_H,
            transform: `scale(${contentScale})`,
            transformOrigin: 'top left',
            '--page-w': `${MOBILE_CANVAS_W}px`,
            '--page-h': `${MOBILE_CANVAS_H}px`,
            '--vw': `${MOBILE_CANVAS_W / 100}px`,
            '--vh': `${MOBILE_CANVAS_H / 100}px`,
          }}>
            {page}
          </div>
        </div>
      ))}
    </div>
  );
});
const BookEngine = React.memo(BookEngineInner, () => true);

const ARTICLES = {
  'a-machine-that-shouldnt-exist': {
    images: {
      hero: `${IMG}/01_hero_seaside_front_34.png`,
      detail: `${IMG}/02_front_detail_dp_stripe.png`,
      rearLogo: `${IMG}/03_do_kremer_rear_logo.png`,
      rear34: `${IMG}/03_rear_34_wooded_scene.png`,
      engine: `${IMG}/04_engine_rear_open.png`,
      wheel: `${IMG}/05_wheel_detail.png`,
      plaque1: `${IMG}/10_commission_plaque.png`,
      plaque2: `${IMG}/page5_img2.jpeg`,
      interior: `${IMG}/07_interior_seats.png`,
      rollcage: `${IMG}/11_interior_rollcage.png`,
      closing: `${IMG}/09_rear_closing.png`,
    }
  },
  'the-30-who-were-right': {
    images: {
      cover: '/articles/ferrari-599/01_cover.png',
      openingReveal: '/articles/ferrari-599/02_opening_reveal.png',
      sideProfile: '/articles/ferrari-599/03_side_profile.png',
      presence: '/articles/ferrari-599/04_presence.jpg',
      identity: '/articles/ferrari-599/05_identity.png',
      wheel: '/articles/ferrari-599/06_wheel.png',
      interiorEntry: '/articles/ferrari-599/07_interior_entry.png',
      cabin: '/articles/ferrari-599/08_cabin.png',
      seats: '/articles/ferrari-599/09_seats.png',
      tactile: '/articles/ferrari-599/10_tactile.png',
      conversion: '/articles/ferrari-599/11_conversion.png',
      ending: '/articles/ferrari-599/12_ending.png',
      endPage: '/articles/ferrari-599/13_revs_tales_end.png',
    }
  }
};

/* ═══════════════════════════════════════════
   PAPER GRAIN
   ═══════════════════════════════════════════ */
const GrainFilter = () => (
  <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
    <defs>
      <filter id="pg">
        <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
    </defs>
  </svg>
);

const Grain = () => (
  <svg
    style={{
      position: 'absolute', inset: 0,
      width: '100%', height: '100%',
      pointerEvents: 'none',
      opacity: 0.028,
      mixBlendMode: 'soft-light',
      zIndex: 1,
    }}
    aria-hidden="true"
  >
    <rect width="100%" height="100%" filter="url(#pg)" />
  </svg>
);

/* ═══════════════════════════════════════════
   FIXED CANVAS
   ═══════════════════════════════════════════ */
const Page = ({ children, bg = '#F4F3EF', testId, grain = true }) => (
  <div
    className="article-page"
    data-testid={testId}
    style={{
      width: 'var(--page-w, 100vw)',
      height: 'var(--page-h, 100vh)',
      overflow: 'hidden',
      position: 'relative',
      background: bg,
    }}
  >
    {grain && <Grain />}
    {children}
  </div>
);

/* body text — ~12% larger (16px from 14px), increased line height */
const body = (extra = {}) => ({
  fontSize: 16,
  lineHeight: 1.85,
  color: '#444',
  marginBottom: 22,
  ...extra,
});

/* ═══════════════════════════════════════════
   LANGUAGE SELECTOR
   ═══════════════════════════════════════════ */
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
];

const LanguageSelector = ({ lang, onChangeLang }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'fixed', top: 20, left: 24, zIndex: 30 }} data-testid="lang-selector">
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          border: 'none', borderRadius: 20, padding: '8px 14px',
          color: '#fff', fontSize: 12, fontWeight: 500, letterSpacing: '0.08em',
          cursor: 'pointer', opacity: 0.85, transition: 'opacity 0.2s ease, background 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(0,0,0,0.55)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
        data-testid="lang-selector-btn"
      >
        <Globe size={14} strokeWidth={1.8} />
        {lang.toUpperCase()}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 10, overflow: 'hidden', minWidth: 140,
        }}>
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => { onChangeLang(l.code); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: lang === l.code ? 'rgba(255,255,255,0.12)' : 'transparent',
                border: 'none', padding: '10px 16px',
                color: '#fff', fontSize: 13, fontWeight: lang === l.code ? 600 : 400,
                cursor: 'pointer', transition: 'background 0.15s ease',
                letterSpacing: '0.03em',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = lang === l.code ? 'rgba(255,255,255,0.12)' : 'transparent'; }}
              data-testid={`lang-option-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   LIGHTBOX
   ═══════════════════════════════════════════ */
const Lightbox = ({ src, onClose }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      data-testid="lightbox-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.94)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <img
        src={src} alt=""
        style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', pointerEvents: 'none' }}
        data-testid="lightbox-image"
      />
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        style={{
          position: 'absolute', top: 24, right: 24,
          color: '#fff', opacity: 0.6, background: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <X size={28} />
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MOBILE LIGHTBOX — Enhanced image viewer
   Pinch-to-zoom, pan, tap-to-close.
   Fully isolated touch handling.
   ═══════════════════════════════════════════ */
const MobileLightbox = ({ src, onClose }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const stateRef = useRef({ zoom: 1, panX: 0, panY: 0 });

  const applyTransform = useCallback(() => {
    if (!imgRef.current) return;
    const { zoom, panX, panY } = stateRef.current;
    imgRef.current.style.transform = zoom > 1
      ? `translate3d(${panX}px, ${panY}px, 0) scale(${zoom})`
      : 'translate3d(0,0,0) scale(1)';
  }, []);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const s = stateRef.current;
    let mode = null;
    const g = {
      startX: 0, startY: 0,
      pinchDist0: 0, zoom0: 1,
      panStartX: 0, panStartY: 0,
      panOffX: 0, panOffY: 0,
    };

    const onTS = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.touches.length >= 2) {
        mode = 'PINCH';
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        g.pinchDist0 = Math.hypot(dx, dy);
        g.zoom0 = s.zoom;
      } else if (s.zoom > 1.05) {
        mode = 'PAN';
        g.panStartX = e.touches[0].clientX;
        g.panStartY = e.touches[0].clientY;
        g.panOffX = s.panX;
        g.panOffY = s.panY;
      } else {
        mode = null;
        g.startX = e.touches[0].clientX;
        g.startY = e.touches[0].clientY;
      }
    };

    const onTM = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (mode === 'PINCH' && e.touches.length >= 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        s.zoom = Math.max(1, Math.min(5, g.zoom0 * (dist / g.pinchDist0)));
        if (s.zoom <= 1.05) { s.zoom = 1; s.panX = 0; s.panY = 0; }
        applyTransform();
      } else if (mode === 'PAN' && e.touches.length === 1) {
        s.panX = g.panOffX + (e.touches[0].clientX - g.panStartX);
        s.panY = g.panOffY + (e.touches[0].clientY - g.panStartY);
        applyTransform();
      }
    };

    const onTE = (e) => {
      e.stopPropagation();
      if (mode === null && e.changedTouches.length > 0) {
        const t = e.changedTouches[0];
        const dist = Math.hypot(t.clientX - g.startX, t.clientY - g.startY);
        if (dist < 20) {
          if (s.zoom > 1.05) {
            s.zoom = 1; s.panX = 0; s.panY = 0;
            applyTransform();
          } else {
            onClose();
          }
        }
      }
      if (e.touches.length === 0) mode = null;
    };

    el.addEventListener('touchstart', onTS, { passive: false, capture: true });
    el.addEventListener('touchmove', onTM, { passive: false, capture: true });
    el.addEventListener('touchend', onTE, { passive: false, capture: true });
    return () => {
      el.removeEventListener('touchstart', onTS, { capture: true });
      el.removeEventListener('touchmove', onTM, { capture: true });
      el.removeEventListener('touchend', onTE, { capture: true });
    };
  }, [onClose, applyTransform]);

  return (
    <div
      ref={containerRef}
      data-testid="mobile-lightbox-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.96)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt=""
        style={{
          display: 'block',
          maxWidth: '95vw', maxHeight: '95vh',
          width: 'auto', height: 'auto',
          objectFit: 'contain',
          transformOrigin: 'center center',
          transform: 'translate3d(0,0,0) scale(1)',
          pointerEvents: 'none',
        }}
        data-testid="mobile-lightbox-image"
      />
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="mobile-lightbox-close"
        style={{
          position: 'absolute', top: 20, right: 20,
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 210,
        }}
      >
        <X size={22} color="#fff" style={{ opacity: 0.8 }} />
      </button>
      <div style={{
        position: 'absolute', bottom: 24, left: '50%',
        transform: 'translateX(-50%)',
        fontSize: 11, letterSpacing: '0.1em',
        color: 'rgba(255,255,255,0.3)',
        pointerEvents: 'none',
      }}>
        PINCH TO ZOOM
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PAGE 1 — HERO (FULL BLEED IMAGE)
   ═══════════════════════════════════════════ */
const P1 = ({ t, img, onOpen }) => (
  <Page testId="page-hero" bg="#0a0a0a" grain={false}>
    <img
      src={img.hero}
      alt="1984 DP-Kremer Porsche 935"
      data-img-zone
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        objectFit: 'cover',
        objectPosition: FOCAL.hero,
        cursor: 'zoom-in',
      }}
    />
    <div style={{
      position: 'absolute', inset: 0,
      background: 'linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.28) 32%, transparent 55%)',
      pointerEvents: 'none',
    }} />
    <div className="hero-text-overlay" style={{
      position: 'absolute',
      top: 'calc(var(--vh, 1vh) * 10)', left: 'calc(var(--vw, 1vw) * 5)',
      maxWidth: 'calc(var(--vw, 1vw) * 30)',
      zIndex: 2,
    }}>
      <span style={{ display: 'block', fontSize: 10, letterSpacing: '0.35em', color: 'rgba(255,255,255,0.5)', marginBottom: 8, textTransform: 'uppercase', fontWeight: 300 }}>
        Revs Tales
      </span>
      <span style={{ display: 'block', fontSize: 'clamp(24px, calc(var(--vw, 1vw) * 2.5), 36px)', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.75)', marginBottom: 8, fontWeight: 500, lineHeight: 1.25 }}>
        {t.hero.title}
      </span>
      <h1 style={{ fontSize: 'clamp(32px, calc(3.8 * var(--vw, 1vw)), 56px)', fontWeight: 700, color: '#fff', lineHeight: 1.12, marginBottom: 12 }} data-testid="article-headline">
        {t.hero.headline}
      </h1>
      <p data-testid="article-subheadline" style={{ fontSize: 'clamp(14px, calc(var(--vw, 1vw) * 1.3), 18px)', fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
        {t.hero.subheadline}
      </p>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 2 — INTRO + BEGINNING (SPLIT)
   Text left, detail image right (LOCKED)
   image_id: 02_front_detail_dp_stripe
   ═══════════════════════════════════════════ */
const P2 = ({ t, img, onOpen }) => (
  <Page testId="page-intro">
    <div className="article-split-grid article-img-after" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 7) calc(var(--vw, 1vw) * 4) 0 calc(var(--vw, 1vw) * 5)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 600, paddingBottom: 140, position: 'relative', zIndex: 2 }}>
          {t.opening.map((text, i) => (
            <p key={i} style={body({ fontSize: 17, lineHeight: 1.9, marginBottom: 26 })}>{text}</p>
          ))}
        </div>
      </div>
      <div
        className="article-img-col"
        style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in', minHeight: '100%', background: '#e8e6e2' }}
        data-img-zone
        data-testid="p2-image-lock"
      >
        <img
          src={img.detail}
          alt="Front detail — DP stripe and logo"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '42% center' }}
          data-testid="article-detail-image"
        />
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 3 — THE BEGINNING (SPLIT)
   Rear logo image left (LOCKED), text right
   image_id: 03_do_kremer_rear_logo
   ═══════════════════════════════════════════ */
const P3 = ({ t, img, onOpen }) => (
  <Page testId="page-condition">
    <div className="article-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div
        className="article-img-col"
        style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in', minHeight: '100%', background: '#e8e6e2' }}
        onClick={() => onOpen(img.rearLogo)}
        data-testid="p3-image-lock"
        data-img-zone
      >
        <img
          src={img.rearLogo}
          alt="Rear wing — DP and Kremer logos"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: FOCAL.rearLogo }}
          data-testid="article-rear-logo-image"
        />
      </div>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 7) calc(var(--vw, 1vw) * 5) 0 calc(var(--vw, 1vw) * 4)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 600, paddingBottom: 140, position: 'relative', zIndex: 2 }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: '#111', marginBottom: 24 }}>{t.theBeginning.title}</h2>
          {t.theBeginning.beforeImage.map((text, i) => (
            <p key={`b${i}`} style={body()}>{text}</p>
          ))}
          {t.theBeginning.afterImage.map((text, i) => (
            <p key={`a${i}`} style={body()}>{text}</p>
          ))}
        </div>
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — PAGE 2 (Opening reveal banner + text)
   Image at top (16:9, full container width), text below
   ═══════════════════════════════════════════ */
const F599P2Opening = ({ t, img, onOpen }) => (
  <Page testId="page-intro">
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', paddingTop: 'calc(var(--vh, 1vh) * 5)', paddingLeft: 'calc(var(--vw, 1vw) * 5)', paddingRight: 'calc(var(--vw, 1vw) * 5)', paddingBottom: 140, position: 'relative', zIndex: 2 }}>
        <div
          style={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden', marginBottom: 36, cursor: 'zoom-in' }}
          data-img-zone
          onClick={() => onOpen(img.detail)}
          data-testid="f599-opening-reveal-zone"
        >
          <img
            src={img.detail}
            alt="Ferrari 599 GTB — opening reveal"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }}
            data-testid="f599-opening-reveal-image"
          />
        </div>
        {t.opening.map((text, i) => (
          <p key={i} style={body({ fontSize: 17, lineHeight: 1.9, marginBottom: 26 })}>{text}</p>
        ))}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — PAGE 3 (Text-only: "The Ones Who Refused")
   ═══════════════════════════════════════════ */
const F599P3Text = ({ t }) => (
  <Page testId="page-condition">
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingTop: 'calc(var(--vh, 1vh) * 7)', paddingLeft: 'calc(var(--vw, 1vw) * 5)', paddingRight: 'calc(var(--vw, 1vw) * 5)', paddingBottom: 140, position: 'relative', zIndex: 2 }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#111', marginBottom: 24 }}>{t.theBeginning.title}</h2>
        {t.theBeginning.beforeImage.map((text, i) => (
          <p key={`b${i}`} style={body()}>{text}</p>
        ))}
        {t.theBeginning.afterImage.map((text, i) => (
          <p key={`a${i}`} style={body()}>{text}</p>
        ))}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — FULL-BLEED (light background, reusable)
   ═══════════════════════════════════════════ */
const F599FullBleed = ({ imgSrc, onOpen, mobile, testId = "page-f599-fullbleed", bg = "#e8e6e2", imgPosition = "center center", imgFit }) => (
  <Page testId={testId} bg={bg} grain={false}>
    <div data-img-zone style={{
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img
        src={imgSrc}
        alt=""
        onClick={() => onOpen(imgSrc)}
        style={{
          width: '100%', height: '100%',
          objectFit: imgFit || (mobile ? 'contain' : 'cover'),
          objectPosition: imgPosition,
          cursor: 'zoom-in',
        }}
        data-testid={`${testId}-img`}
      />
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — PAGE 8: P6 text + two stacked images (identity + wheel)
   Text layout matches P6 exactly, image col uses PPlaque-style stacking
   ═══════════════════════════════════════════ */
const F599P6Dual = ({ t, img1, img2, onOpen }) => (
  <Page testId="page-rewriting">
    <div className="article-split-grid article-img-after" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 6) calc(var(--vw, 1vw) * 4) 0 calc(var(--vw, 1vw) * 5)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 580, paddingBottom: 140, position: 'relative', zIndex: 2 }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 22 }}>{t.rewritingMachine.title}</h2>
          {t.rewritingMachine.beforeImage.map((text, i) => (
            <p key={`b${i}`} style={body({ fontSize: 15 })}>{text}</p>
          ))}
          {t.rewritingMachine.afterImage.map((text, i) => (
            <p key={`a${i}`} style={body({ fontSize: 15 })}>{text}</p>
          ))}
        </div>
      </div>
      <div className="article-img-col" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 0, margin: 0, minHeight: 0 }}>
        <div
          style={{ flex: 1, width: '100%', overflow: 'hidden', padding: 0, margin: 0, cursor: 'zoom-in', background: '#e8e6e2' }}
          onClick={() => onOpen(img1)}
          data-img-zone
        >
          <img src={img1} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center center', transform: 'scale(0.85)', display: 'block' }} data-testid="f599-p6-img1" />
        </div>
        <div style={{ height: 44, flexShrink: 0 }} />
        <div
          style={{ flex: 1, width: '100%', overflow: 'hidden', padding: 0, margin: 0, cursor: 'zoom-in', background: '#e8e6e2' }}
          onClick={() => onOpen(img2)}
          data-img-zone
        >
          <img src={img2} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center center', transform: 'scale(0.82)', display: 'block' }} data-testid="f599-p6-img2" />
        </div>
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — PAGE 10: PPlaque text + single full-height image (seats)
   Text layout matches PPlaque exactly, right col is one full image
   ═══════════════════════════════════════════ */
const F599PlaqueSingle = ({ textParagraphs, imgSrc, onOpen }) => (
  <Page testId="page-plaque">
    <div className="article-split-grid article-img-after" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 7) calc(var(--vw, 1vw) * 6) 0 calc(var(--vw, 1vw) * 5)', overflowY: 'auto', minHeight: 0 }}>
        <div style={{ maxWidth: 660, paddingBottom: 100, position: 'relative', zIndex: 2 }}>
          {textParagraphs.map((para, i) => (
            <p key={i} style={body({ fontSize: 15 })}>{para}</p>
          ))}
        </div>
      </div>
      <div
        className="article-img-col"
        style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
        onClick={() => onOpen(imgSrc)}
        data-img-zone
      >
        <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block' }} data-testid="f599-plaque-single-img" />
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — FINAL TEXT PAGE + CREDITS
   Scrollable multi-section layout for the F599 conclusion
   ═══════════════════════════════════════════ */
const F599FinalText = ({ sections, closingLine, credits, testId = "page-final-text" }) => (
  <Page testId={testId}>
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingTop: 'calc(var(--vh, 1vh) * 7)', paddingLeft: 'calc(var(--vw, 1vw) * 5)', paddingRight: 'calc(var(--vw, 1vw) * 5)', paddingBottom: 180, position: 'relative', zIndex: 2 }}>
        {sections.map((section, si) => (
          <div key={si} style={{ marginTop: si > 0 ? 36 : 0 }}>
            {section.title && <h2 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 20 }}>{section.title}</h2>}
            {section.paragraphs.map((text, i) => (
              <p key={i} style={body({ fontSize: 15, lineHeight: 1.8 })}>{text}</p>
            ))}
          </div>
        ))}
        {closingLine && (
          <p style={{ fontSize: 20, lineHeight: 1.7, fontWeight: 500, color: '#222', fontStyle: 'italic', marginTop: 36, marginBottom: 0 }}>
            {closingLine}
          </p>
        )}
        {credits && (
          <div style={{ marginTop: 56, fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#222', opacity: 0.75, textAlign: 'left' }} data-testid="article-credits">
            <p style={{ margin: 0 }}>{credits.written}</p>
            <p style={{ margin: 0 }}>{credits.photography}</p>
            <p style={{ margin: 0 }}>{credits.cover}</p>
            <p style={{ margin: '16px 0 0' }}>{credits.thanks}</p>
          </div>
        )}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   F599 — CLOSING STATEMENT PAGE
   Body text → dramatic emphasis → tagline → credits
   ═══════════════════════════════════════════ */
const F599ClosingStatement = ({ bodyParagraphs, emphasisLines, taglineLines, credits }) => (
  <Page testId="page-f599-closing">
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{
        maxWidth: 660, margin: '0 auto',
        paddingTop: 'calc(var(--vh, 1vh) * 8)',
        paddingLeft: 'calc(var(--vw, 1vw) * 5)',
        paddingRight: 'calc(var(--vw, 1vw) * 5)',
        paddingBottom: 80,
        position: 'relative', zIndex: 2,
      }}>
        {bodyParagraphs?.map((text, i) => (
          <p key={i} style={{ fontSize: 16, lineHeight: 1.85, fontWeight: 400, color: '#333', marginBottom: 24 }}>{text}</p>
        ))}
        {emphasisLines && (
          <div style={{ marginTop: 44 }}>
            {emphasisLines.map((line, i) => (
              <p key={i} style={{
                fontSize: 22, lineHeight: 1.5, fontWeight: 600,
                color: '#111', fontStyle: 'italic',
                marginTop: i > 0 ? 6 : 0, marginBottom: 0,
              }}>{line}</p>
            ))}
          </div>
        )}
        {taglineLines && (
          <div style={{ marginTop: 44 }}>
            {taglineLines.map((line, i) => (
              <p key={i} style={{
                fontSize: 17, lineHeight: 1.6, fontWeight: 500,
                color: '#444', fontStyle: 'italic',
                marginTop: i > 0 ? 6 : 0, marginBottom: 0,
              }}>{line}</p>
            ))}
          </div>
        )}
        {credits && (
          <div style={{ marginTop: 56, fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#222', opacity: 0.75, textAlign: 'left' }} data-testid="article-credits-closing">
            <p style={{ margin: 0 }}>{credits.written}</p>
            <p style={{ margin: 0 }}>{credits.photography}</p>
            <p style={{ margin: '16px 0 0' }}>{credits.thanks}</p>
          </div>
        )}
      </div>
    </div>
  </Page>
);


/* ═══════════════════════════════════════════
   PAGE 4 — A CONDITION, NOT A REQUEST (TEXT)
   ═══════════════════════════════════════════ */
const PCondition = ({ t }) => (
  <Page testId="page-condition-text">
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingTop: 'calc(var(--vh, 1vh) * 7)', paddingLeft: 'calc(var(--vw, 1vw) * 5)', paddingRight: 'calc(var(--vw, 1vw) * 5)', paddingBottom: 180, position: 'relative', zIndex: 2 }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#111', marginBottom: 22 }}>{t.aCondition.title}</h2>
        {t.aCondition.paragraphs.map((text, i) => (
          <p key={i} style={body({ fontSize: 16, lineHeight: 1.85 })}>{text}</p>
        ))}
        {t.theProject.map((text, i) => (
          <p key={`p${i}`} style={body({ fontSize: 16, lineHeight: 1.85 })}>{text}</p>
        ))}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 5 — FULL BLEED IMAGE (rear 3/4)
   ═══════════════════════════════════════════ */
const P4 = ({ img, onOpen, mobile }) => (
  <Page testId="page-image-rear" bg="#1a1a18" grain={false}>
    <div data-img-zone style={{
      position: 'absolute',
      top: mobile ? 0 : '-30%',
      left: 0, right: 0, bottom: 0,
      display: mobile ? 'flex' : 'block',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img
        src={img.rear34}
        alt="Rear three-quarter — wooded scene"
        onClick={() => onOpen(img.rear34)}
        style={{
          width: '100%', height: '100%',
          objectFit: mobile ? 'contain' : 'cover',
          objectPosition: mobile ? 'center center' : 'center bottom',
          cursor: 'zoom-in',
        }}
      />
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 5 — THE SHAPE OF INTENT (TEXT PAGE)
   ═══════════════════════════════════════════ */
const P5 = ({ t }) => (
  <Page testId="page-shape">
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingTop: 'calc(var(--vh, 1vh) * 7)', paddingLeft: 'calc(var(--vw, 1vw) * 5)', paddingRight: 'calc(var(--vw, 1vw) * 5)', paddingBottom: 140, position: 'relative', zIndex: 2 }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: '#111', marginBottom: 24 }}>{t.shapeOfIntent.title}</h2>
        {t.shapeOfIntent.paragraphs.map((text, i) => (
          <p key={i} style={body({ fontSize: 16, lineHeight: 1.85 })}>{text}</p>
        ))}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 6 — REWRITING THE MACHINE (SPLIT)
   Text left, Engine right
   ═══════════════════════════════════════════ */
const P6 = ({ t, img, onOpen }) => (
  <Page testId="page-rewriting">
    <div className="article-split-grid article-img-after" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 6) calc(var(--vw, 1vw) * 4) 0 calc(var(--vw, 1vw) * 5)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 580, paddingBottom: 140, position: 'relative', zIndex: 2 }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 22 }}>{t.rewritingMachine.title}</h2>
          {t.rewritingMachine.beforeImage.map((text, i) => (
            <p key={`b${i}`} style={body({ fontSize: 15 })}>{text}</p>
          ))}
          {t.rewritingMachine.afterImage.map((text, i) => (
            <p key={`a${i}`} style={body({ fontSize: 15 })}>{text}</p>
          ))}
        </div>
      </div>
      <div
        className="article-img-col"
        style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
        onClick={() => onOpen(img.engine)}
        data-img-zone
      >
        <img
          src={img.engine}
          alt="Engine bay — Kremer Racing"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: FOCAL.engine }}
          data-testid="article-engine-image"
        />
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 7 — THE HUMAN DETAILS (SPLIT)
   Wheel left, Text right (scrollable)
   ═══════════════════════════════════════════ */
const P7 = ({ t, img, onOpen }) => (
  <Page testId="page-human">
    <div className="article-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div
        className="article-img-col"
        style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
        onClick={() => onOpen(img.wheel)}
        data-img-zone
      >
        <img
          src={img.wheel}
          alt="BBS wheel — gold centers"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: FOCAL.wheel }}
          data-testid="article-wheel-image"
        />
      </div>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 6) calc(var(--vw, 1vw) * 5) 0 calc(var(--vw, 1vw) * 4)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 580, paddingBottom: 140, position: 'relative', zIndex: 2 }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 22 }}>{t.humanDetails.title}</h2>
          {t.humanDetails.intro.map((text, i) => (
            <p key={`i${i}`} style={body({ fontSize: 15 })}>{text}</p>
          ))}
          {t.humanDetails.wheelText.map((text, i) => (
            <p key={`w${i}`} style={body({ fontSize: 15 })}>{text}</p>
          ))}
        </div>
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 8 — DETAIL IMAGE (FULL BLEED)
   ═══════════════════════════════════════════ */
const P8 = ({ img, onOpen }) => (
  <Page testId="page-detail" bg="#0a0a0a" grain={false}>
    <img
      src={img.interior}
      alt="Interior — Recaro seats"
      data-img-zone
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        objectFit: 'cover',
        objectPosition: FOCAL.interior,
        cursor: 'zoom-in',
      }}
      data-testid="article-interior-image"
    />
  </Page>
);

/* ═══════════════════════════════════════════
   NEW PAGE 10 — PLAQUE DETAIL (SPLIT)
   Verbatim text left, two plaque images stacked right
   ═══════════════════════════════════════════ */
const PLAQUE_TEXT = `The interior was all tailor made to Dr. Paletta's specifications and tastes. An engraved "935 K3 Street Turbo" commission plaque on the passenger door, and a serialized plaque on the glovebox confirming that this unit is the first ever of the G5 Street 2 series to have been built are present. This is where chassis number BZ 03 010 only starts to really stand out from the other five DP-Kremer Porsche 935 Group 5 K3 Street2 built and what ensure it remains a true one-off.

For example, Dr. Paletta was a music connoisseur and loved opera. He didn't just want an upgraded sound system. He wanted the best sound system available at the time. Kremer did not offer any sound systems originally. After some deliberations and recommendations from Kremer they settled on a 20-speaker sound system from Sony. Only a handful of these were in the market at the time. A man of vast resources, Dr. Paletta found one for sale in St. Louis, MO and shipped it to Cologne, Germany as the car was being built. Kremer originally installed six of those speakers in the rear parcel shelf alone. By the time they were done fitting the audio system, they had won the Guinness World Record for the greatest number of speakers ever installed to a car up to that time! Sadly, at some point under Dr. Paletta's stewardship, these were removed.`;

const ROLLCAGE_TEXT = `Kremer initially wanted to install a full roll-cage as they did on other builds. Dr. Paletta insisted on just doing a partial roll-cage that had to be wrapped in leather as he did not want to climb in and out of the car. Dr. Paletta found the original Kremer offer of installing racing seats too uncomfortable. Therefore, this too was modified with much more comfortable power-adjustable heated leather Recaro sports bucket seats. Because of the way that the gearbox was mounted upside down to reduce the axle angle, it made the rear seats too high to use. Not to mention that the roll cage makes the rear seats entirely unusable. Why were the rear seats not removed you may ask? Dr. Paletta had Kremer reinstall those rear seats back in only because he wanted a place to store his brief case.

A racing fuel cell wasn't used as Kremer wanted because Dr. Paletta quite simply wanted luggage space. Because of this, the normal 930 gas tank remained. Other accessories include an HK LED boost gauge, three-spoke leather steering wheel, power windows, a heater, fender-mounted antenna, electric sunroof, and rear window wiper. Creature comforts were a must for Dr. Paletta. However, he also requested a much necessary option for when he would drive spiritedly... the newly released for 1984 Escort Passport 5000 radar detector. This was the best radar detector available at the time, and with his vast resources, Dr. Paletta sourced one of the first ones ever offered to the market. Though outdated by modern standards, the front, top, and rear radar detectors can still be found with chassis number BZ 03 010 to this day.`;

const PPlaque = ({ img, onOpen, textParagraphs }) => (
  <Page testId="page-plaque">
    <div className="article-split-grid article-img-after" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 7) calc(var(--vw, 1vw) * 6) 0 calc(var(--vw, 1vw) * 5)', overflowY: 'auto', minHeight: 0 }}>
        <div style={{ maxWidth: 660, paddingBottom: 100, position: 'relative', zIndex: 2 }}>
          {(textParagraphs || PLAQUE_TEXT.split('\n\n')).map((para, i) => (
            <p key={i} style={body({ fontSize: 15 })}>{para}</p>
          ))}
        </div>
      </div>
      <div className="article-img-col" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 0, margin: 0, minHeight: 0 }}>
        <div
          style={{ flex: 1, width: '100%', overflow: 'hidden', padding: 0, margin: 0, cursor: 'zoom-in' }}
          onClick={() => onOpen(img.plaque1)}
          data-img-zone
        >
          <img
            src={img.plaque1}
            alt="Commission plaque — KREMER G5 Street 2"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 45%', display: 'block' }}
            data-testid="article-plaque1-image"
          />
        </div>
        <div style={{ height: 44, flexShrink: 0 }} />
        <div
          style={{ flex: 1, width: '100%', overflow: 'hidden', padding: 0, margin: 0, cursor: 'zoom-in' }}
          onClick={() => onOpen(img.plaque2)}
          data-img-zone
        >
          <img
            src={img.plaque2}
            alt="Serialized plaque — chassis number"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 50%', display: 'block' }}
            data-testid="article-plaque2-image"
          />
        </div>
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 11 — ROLLCAGE / LIVABILITY (split: image + text)
   ═══════════════════════════════════════════ */
const PRollcage = ({ img, onOpen, textParagraphs, title, imgFit = 'cover', imgPosition = 'center 42%' }) => (
  <Page testId="page-rollcage">
    <div className="article-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' }}>
      <div
        className="article-img-col"
        style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in', display: 'flex', alignItems: 'stretch' }}
        onClick={() => onOpen(img.rollcage)}
        data-img-zone
      >
        <img
          src={img.rollcage}
          alt="Interior — partial roll-cage and Recaro seats"
          style={{ width: '100%', height: 'var(--page-h, 100vh)', objectFit: imgFit, objectPosition: imgPosition }}
          data-testid="article-rollcage-image"
        />
      </div>
      <div className="article-text-col" style={{ padding: 'calc(var(--vh, 1vh) * 7) calc(var(--vw, 1vw) * 5) 0 calc(var(--vw, 1vw) * 4)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 580, paddingBottom: 180, position: 'relative', zIndex: 2 }}>
          {title && <h2 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 22 }}>{title}</h2>}
          {(textParagraphs || ROLLCAGE_TEXT.split('\n\n')).map((para, i) => (
            <p key={i} style={body()}>{para}</p>
          ))}
        </div>
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 12 — FINAL TEXT (delivered + closing)
   ═══════════════════════════════════════════ */
const P9 = ({ t, inlineImg, onOpen }) => (
  <Page testId="page-final">
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingTop: 'calc(var(--vh, 1vh) * 7)', paddingLeft: 'calc(var(--vw, 1vw) * 5)', paddingRight: 'calc(var(--vw, 1vw) * 5)', paddingBottom: 180, position: 'relative', zIndex: 2 }}>
        {inlineImg && (
          <div
            onClick={() => onOpen?.(inlineImg)}
            data-img-zone
            style={{ marginBottom: 28, cursor: 'zoom-in', borderRadius: 2, overflow: 'hidden' }}
          >
            <img src={inlineImg} alt="" style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover' }} />
          </div>
        )}
        <h2 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 20 }}>{t.delivered.title}</h2>
        {t.delivered.paragraphs.map((text, i) => (
          <p key={`d${i}`} style={body({ fontSize: 15, lineHeight: 1.8 })}>{text}</p>
        ))}
        <div style={{ height: 28 }} />
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111', marginBottom: 18 }}>{t.afterOwnership.title}</h2>
        {t.afterOwnership.paragraphs.map((text, i) => (
          <p key={`a${i}`} style={body({ fontSize: 15, lineHeight: 1.8 })}>{text}</p>
        ))}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 10 — FINAL IMAGE (FULL BLEED)
   ═══════════════════════════════════════════ */
const P10 = ({ img, onOpen }) => (
  <Page testId="page-closing" bg="#1a1a18" grain={false}>
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img
        src={img.closing}
        alt="Rear view — closing"
        data-img-zone
        style={{
          width: '100%',
          maxWidth: 'calc(var(--page-h, 100vh) * 1.592)',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center center',
          cursor: 'zoom-in',
        }}
        data-testid="article-closing-image"
      />
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 15 — CLOSING STATEMENT (standalone)
   ═══════════════════════════════════════════ */
const PClosingStatement = ({ closingLines, credits }) => (
  <Page testId="page-closing-statement">
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      height: 'var(--page-h, 100vh)', width: '100%',
      boxSizing: 'border-box',
      paddingTop: 'clamp(8px, calc(var(--vh, 4px) * 2.5), 32px)',
      paddingBottom: 'clamp(8px, calc(var(--vh, 4px) * 3), 40px)',
    }}>
      <div style={{
        maxWidth: 'min(720px, calc(var(--page-w, 1080px) * 0.82))',
        margin: '0 auto',
        paddingLeft: 'clamp(20px, calc(var(--vw, 8px) * 4), 80px)',
        paddingRight: 'clamp(20px, calc(var(--vw, 8px) * 4), 80px)',
        position: 'relative', zIndex: 2,
      }}>
        {closingLines ? (
          closingLines.map((line, i) => (
            <p key={i} style={{
              fontSize: 'clamp(12px, calc(var(--vh, 4px) * 3), 20px)',
              lineHeight: 1.5, fontWeight: 500,
              color: '#222', textAlign: 'left',
              marginTop: i > 0 ? 'clamp(8px, calc(var(--vh, 4px) * 2.5), 28px)' : 0,
              marginBottom: 0,
              fontStyle: 'italic',
            }}>
              {line}
            </p>
          ))
        ) : (
          <>
            <p style={{
              fontSize: 'clamp(12px, calc(var(--vh, 4px) * 3), 20px)',
              lineHeight: 1.5, fontWeight: 500,
              color: '#222', textAlign: 'left', marginBottom: 0,
            }}>
              Only six were ever built. This was the first.
            </p>
            <p style={{
              fontSize: 'clamp(12px, calc(var(--vh, 4px) * 3), 20px)',
              lineHeight: 1.5, fontWeight: 500,
              color: '#222', textAlign: 'left',
              marginTop: 'clamp(8px, calc(var(--vh, 4px) * 2.5), 28px)',
              marginBottom: 0,
            }}>
              The one that made the others possible. It passed through hands. It was driven. It was maintained. But none of that changed what it was. A machine born from insistence. From friction. From the refusal to accept what already existed. And through it all, one truth remained consistent with the first of the only six DP-Kremer Porsche 935 Group 5 K3 Street2 ever built: <em><strong>It is not a car that sits quietly. It is a car that asks something of you.</strong></em>
            </p>
          </>
        )}
        {credits ? (
          <div style={{
            marginTop: 'clamp(10px, calc(var(--vh, 4px) * 4), 56px)',
            fontSize: 'clamp(9px, calc(var(--vh, 4px) * 1.8), 14px)',
            fontWeight: 400, lineHeight: 1.35, color: '#222', opacity: 0.75, textAlign: 'left',
          }} data-testid="article-credits">
            <p style={{ margin: 0 }}>{credits.written}</p>
            <p style={{ margin: 0 }}>{credits.photography}</p>
            <p style={{ margin: 0 }}>{credits.cover}</p>
            <p style={{ margin: 'clamp(6px, calc(var(--vh, 4px) * 1.5), 16px) 0 0' }}>{credits.thanks}</p>
          </div>
        ) : (
          <div style={{
            marginTop: 'clamp(10px, calc(var(--vh, 4px) * 4), 56px)',
            fontSize: 'clamp(9px, calc(var(--vh, 4px) * 1.8), 14px)',
            fontWeight: 400, lineHeight: 1.35, color: '#222', opacity: 0.75, textAlign: 'left',
          }} data-testid="article-credits">
            <p style={{ margin: 0 }}>Written by David Castillo</p>
            <p style={{ margin: 0 }}>Photography by Joseph Ray</p>
            <p style={{ margin: 0 }}>Cover image courtesy of Car Cave SRQ</p>
            <p style={{ margin: 'clamp(6px, calc(var(--vh, 4px) * 1.5), 16px) 0 0' }}>With special thanks to the Jacksonville Sports Car Museum</p>
          </div>
        )}
      </div>
    </div>
  </Page>
);

/* ═══════════════════════════════════════════
   PAGE 16 — BRAND PAGE
   ═══════════════════════════════════════════ */
const P11 = ({ t, logoSrc }) => (
  <Page testId="page-brand" bg="#000" grain={false}>
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 1.2s ease-out',
    }}>
      <img
        src={logoSrc || `${IMG}/revs_tales_logo.png`}
        alt="Revs Tales"
        style={{ width: 'calc(var(--vw, 1vw) * 28)', maxWidth: 400, minWidth: 200, height: 'auto', userSelect: 'none' }}
        data-testid="brand-logo"
      />
    </div>
    <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
  </Page>
);

/* F599 components removed — Ferrari 599 now reuses the 935 spread components directly */


/* ═══════════════════════════════════════════
   FLIPBOOK ENGINE
   ═══════════════════════════════════════════ */
const ArticlePage = () => {
  const { slug } = useParams();
  const [articleLang, setArticleLang] = useState('en');
  const article = ARTICLES[slug];
  const [cur, setCur] = useState(0);
  const [anim, setAnim] = useState('');
  const [next, setNext] = useState(null);
  const [dir, setDir] = useState(1);
  const [hover, setHover] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const touchRef = useRef(null);
  const pageRef = useRef(null);
  const flipBookRef = useRef(null);
  const flipContainerRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const contentScaleRef = useRef(1);
  const lightboxSrcRef = useRef(null);
  const momentumId = useRef(null);

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1200);
  const [vpSize, setVpSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1440,
    h: typeof window !== 'undefined' ? window.innerHeight : 900,
  }));
  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth < 1200);
      setVpSize({ w: window.innerWidth, h: window.innerHeight });
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  /* Sync refs for native event listeners (closures can't see React state) */
  useEffect(() => { zoomRef.current = zoomLevel; }, [zoomLevel]);
  useEffect(() => { panRef.current = panPos; }, [panPos]);
  useEffect(() => { lightboxSrcRef.current = lightboxSrc; }, [lightboxSrc]);

  /* Landscape view mode: 'full' (max scale) or 'framed' (current aesthetic) */
  const [landscapeMode, setLandscapeMode] = useState('full');

  /* Immersive mode: hides all non-essential UI, attempts browser fullscreen */
  const [immersive, setImmersive] = useState(false);

  /* Portrait arrow reveal: dims at rest, brightens on any touch, fades after idle */
  const [arrowsRevealed, setArrowsRevealed] = useState(false);
  const arrowTimerRef = useRef(null);

  const enterImmersive = useCallback(() => {
    setImmersive(true);
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } catch (_) {}
  }, []);

  const exitImmersive = useCallback(() => {
    setImmersive(false);
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setImmersive(false);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  /* Portrait immersive arrow reveal: brighten on ANY touch, fade after idle */
  useEffect(() => {
    if (!isMobile) return;
    const reveal = () => {
      setArrowsRevealed(true);
      clearTimeout(arrowTimerRef.current);
      arrowTimerRef.current = setTimeout(() => setArrowsRevealed(false), 2000);
    };
    window.addEventListener('touchstart', reveal, { capture: true, passive: true });
    return () => {
      window.removeEventListener('touchstart', reveal, { capture: true });
      clearTimeout(arrowTimerRef.current);
    };
  }, [isMobile]);

  /* Mobile flip dimensions — computed from viewport size, landscape mode, and immersive state.
     Max mode (immersive or landscape full): scale = min(vpW / pageW, vpH / pageH) — page fills viewport.
     Framed mode: preserves current 85%/90% aesthetic with matte border. */
  const flipDims = useMemo(() => {
    if (!isMobile) return null;
    const pageAR = MOBILE_CANVAS_W / MOBILE_CANVAS_H;
    const isLandscape = vpSize.w > vpSize.h;
    const useMaxScale = immersive || (isLandscape && landscapeMode === 'full');
    let flipW, flipH;
    if (useMaxScale) {
      /* In landscape full-view when NOT immersive, reserve space for:
         - Bottom: zoom toolbar (~60px) + page counter (bottom:54 + 15px text = ~69px)
         - Top: landscape toggle (top:12 + 30px = ~42px)
         - Safe padding: 8px each edge
         SAFE FRAME = viewportHeight - topUI - bottomControls - safePadding */
      const bottomReserve = (isLandscape && !immersive) ? 72 : 0;  /* counter + padding */
      const topReserve    = (isLandscape && !immersive) ? 48 : 0;  /* toggle + padding */
      const safeH = vpSize.h - bottomReserve - topReserve;
      const scale = Math.min(vpSize.w / MOBILE_CANVAS_W, safeH / MOBILE_CANVAS_H);
      flipW = Math.floor(MOBILE_CANVAS_W * scale);
      flipH = Math.floor(MOBILE_CANVAS_H * scale);
    } else {
      const maxWPct = isLandscape ? 0.85 : 0.96;
      /* In framed landscape, reserve space for bottom controls + top UI */
      const bottomReserve = isLandscape ? 72 : 0;
      const topReserve    = isLandscape ? 48 : 0;
      const safeH = vpSize.h - bottomReserve - topReserve;
      const maxHPct = 0.90;
      flipW = Math.floor(vpSize.w * maxWPct);
      flipH = Math.floor(flipW / pageAR);
      if (flipH > safeH * maxHPct) {
        flipH = Math.floor(safeH * maxHPct);
        flipW = Math.floor(flipH * pageAR);
      }
    }
    return { flipW, flipH, contentScale: flipW / MOBILE_CANVAS_W, isLandscape, nativeLayout: !!(useMaxScale && isLandscape) };
  }, [isMobile, vpSize.w, vpSize.h, landscapeMode, immersive]);

  /* Resize BookEngine when flip dimensions change (mode toggle or orientation) */
  const prevFlipDimsRef = useRef(null);
  useEffect(() => {
    if (!flipDims) return;
    const { flipW, flipH, contentScale, nativeLayout } = flipDims;
    const prev = prevFlipDimsRef.current;
    const changed = !prev || prev.w !== flipW || prev.h !== flipH || prev.native !== nativeLayout;
    if (changed && flipBookRef.current?.resize) {
      flipBookRef.current.resize(flipW, flipH, nativeLayout);
      contentScaleRef.current = contentScale;
    }
    prevFlipDimsRef.current = { w: flipW, h: flipH, native: nativeLayout };
  }, [flipDims]);

  /* Reset zoom on page change */
  useEffect(() => {
    if (isMobile) {
      setZoomLevel(1); zoomRef.current = 1;
      setPanPos({ x: 0, y: 0 }); panRef.current = { x: 0, y: 0 };
    }
  }, [cur, isMobile]);

  /* ═══════════════════════════════════════════
     MOBILE GESTURE CONTROLLER (Clean Rebuild)
     ═══════════════════════════════════════════
     Mode-locked touch system:
     - ZOOM: pinch + pan (all library events blocked)
     - PAN: single finger while zoomed (all blocked)
     - SCROLL: vertical on scrollable content (blocked)
     - TURN: library handles natively (real finger curl)
     - null: undecided / tap detection

     Buttons/links: NOT intercepted (browser handles)
     Images: tap opens lightbox, drag becomes library page turn
     ═══════════════════════════════════════════ */
  useEffect(() => {
    if (!isMobile || !flipContainerRef.current) return;
    const el = flipContainerRef.current;

    let mode = null;
    const g = {
      startX: 0, startY: 0, startTime: 0,
      lastY: 0,
      pinchDist0: 0, zoom0: 1,
      panStartX: 0, panStartY: 0, panOffX: 0, panOffY: 0,
      lastTapTime: 0,
      scrollEl: null,
      velocities: [],
      imgSrc: null,
      decided: false,
    };

    function findScrollable(node) {
      while (node && node !== el) {
        if (node.scrollHeight > node.clientHeight + 2) {
          const s = window.getComputedStyle(node);
          if (s.overflowY === 'auto' || s.overflowY === 'scroll') return node;
        }
        node = node.parentElement;
      }
      return null;
    }

    function cancelMomentum() {
      if (momentumId.current) { cancelAnimationFrame(momentumId.current); momentumId.current = null; }
    }

    function applyMomentum(scrollEl, vel) {
      cancelMomentum();
      if (!scrollEl || Math.abs(vel) < 0.5) return;
      let v = vel;
      const step = () => {
        v *= 0.92;
        if (Math.abs(v) < 0.5) { momentumId.current = null; return; }
        scrollEl.scrollTop -= v;
        momentumId.current = requestAnimationFrame(step);
      };
      momentumId.current = requestAnimationFrame(step);
    }

    /* ── TOUCH START (capture phase) ── */
    const onTS = (e) => {
      if (lightboxSrcRef.current) return;
      /* Block all touches during button-triggered curl animation */
      if (flipBookRef.current?.pageFlip()?._curlAnimating) { e.stopPropagation(); e.preventDefault(); return; }
      cancelMomentum();

      /* PINCH ZOOM: 2+ fingers */
      if (e.touches.length >= 2) {
        mode = 'ZOOM';
        g.decided = true;
        e.stopPropagation();
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        g.pinchDist0 = Math.hypot(dx, dy);
        g.zoom0 = zoomRef.current;
        return;
      }

      /* PAN while zoomed */
      if (zoomRef.current > 1.05) {
        mode = 'PAN';
        g.decided = true;
        e.stopPropagation();
        e.preventDefault();
        const t = e.touches[0];
        g.panStartX = t.clientX;
        g.panStartY = t.clientY;
        g.panOffX = panRef.current.x;
        g.panOffY = panRef.current.y;
        return;
      }

      /* Single finger, not zoomed */
      const t = e.touches[0];
      const target = document.elementFromPoint(t.clientX, t.clientY);

      /* BUTTONS/LINKS: let browser handle — no interception */
      if (target?.closest?.('button, a, [role="button"]')) {
        mode = null;
        g.decided = true;
        return;
      }

      /* Record touch info */
      g.startX = t.clientX;
      g.startY = t.clientY;
      g.startTime = Date.now();
      g.lastY = t.clientY;
      g.velocities = [];
      g.decided = false;
      mode = null;

      /* Check for image zone */
      const imgZone = target?.closest?.('[data-img-zone]');
      g.imgSrc = imgZone
        ? (imgZone.querySelector?.('img')?.src || imgZone.src || null)
        : null;

      /* Find scrollable ancestor */
      g.scrollEl = findScrollable(target);

      /* Let library see this touch — it handles TURN gestures natively.
         Only preventDefault to block browser default scroll/zoom. */
      e.preventDefault();
    };

    /* ── TOUCH MOVE (capture phase) ── */
    const onTM = (e) => {
      if (lightboxSrcRef.current) return;

      /* ZOOM: pinch scale */
      if (mode === 'ZOOM') {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length >= 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const dist = Math.hypot(dx, dy);
          const z = Math.max(1, Math.min(3, g.zoom0 * (dist / g.pinchDist0)));
          zoomRef.current = z;
          setZoomLevel(z);
          if (z <= 1.05) { setPanPos({ x: 0, y: 0 }); panRef.current = { x: 0, y: 0 }; }
        }
        return;
      }

      /* PAN while zoomed */
      if (mode === 'PAN') {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 1) {
          const t = e.touches[0];
          const p = {
            x: g.panOffX + (t.clientX - g.panStartX),
            y: g.panOffY + (t.clientY - g.panStartY),
          };
          panRef.current = p;
          setPanPos(p);
        }
        return;
      }

      /* SCROLL: manual scrollTop */
      if (mode === 'SCROLL') {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 1) {
          const t = e.touches[0];
          const deltaY = t.clientY - g.lastY;
          const scale = contentScaleRef.current || 1;
          g.scrollEl.scrollTop -= deltaY / scale;
          const now = Date.now();
          g.velocities.push({ dy: deltaY / scale, t: now });
          while (g.velocities.length > 0 && now - g.velocities[0].t > 120) g.velocities.shift();
          g.lastY = t.clientY;
        }
        return;
      }

      /* TURN: library handles curl natively via its own touch path.
         Only preventDefault to stop browser scroll — do NOT stopPropagation. */
      if (mode === 'TURN') {
        e.preventDefault();
        return;
      }

      /* VERT_BLOCK: pure vertical on non-scrollable — block everything */
      if (mode === 'VERT_BLOCK') {
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      /* UNDECIDED: direction lock on first significant movement */
      if (!g.decided && e.touches.length === 1) {
        const t = e.touches[0];
        const dx = t.clientX - g.startX;
        const dy = t.clientY - g.startY;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);

        if (absDx < 8 && absDy < 8) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }

        g.decided = true;

        /* Vertical → SCROLL or VERT_BLOCK (never becomes a page turn) */
        if (absDy > absDx) {
          if (g.scrollEl) {
            mode = 'SCROLL';
            g.lastY = t.clientY;
          } else {
            mode = 'VERT_BLOCK';
          }
          e.stopPropagation();
          e.preventDefault();
          /* Cancel any library fold state from the un-blocked touchstart */
          try {
            const pf = flipBookRef.current?.pageFlip();
            if (pf) { if (pf.ui) pf.ui.touchPoint = null; pf.userStop({ x: 0, y: 0 }, true); }
          } catch (_) {}
          return;
        }

        /* Horizontal → TURN (library handles both forward and backward
           natively via the convertToPage/convertToGlobal coordinate patch) */
        mode = 'TURN';
        e.preventDefault();
        return;
      }
    };

    /* ── TOUCH END (capture phase) ── */
    const onTE = (e) => {
      if (lightboxSrcRef.current) return;

      /* ZOOM end */
      if (mode === 'ZOOM') {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 0) {
          if (zoomRef.current <= 1.05) {
            zoomRef.current = 1;
            setZoomLevel(1);
            panRef.current = { x: 0, y: 0 };
            setPanPos({ x: 0, y: 0 });
          }
          mode = null;
        }
        return;
      }

      /* PAN end */
      if (mode === 'PAN') {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 0) mode = null;
        return;
      }

      /* SCROLL end → momentum */
      if (mode === 'SCROLL') {
        e.stopPropagation();
        e.preventDefault();
        if (g.velocities.length > 0) {
          const totalDy = g.velocities.reduce((s, v) => s + v.dy, 0);
          const dt = (Date.now() - g.velocities[0].t) || 1;
          const avgVel = (totalDy / dt) * 16;
          applyMomentum(g.scrollEl, avgVel);
        }
        mode = null;
        return;
      }

      /* TURN end → library completes or cancels the flip natively */
      if (mode === 'TURN') {
        e.preventDefault();
        mode = null;
        return;
      }

      /* VERT_BLOCK end → clean up */
      if (mode === 'VERT_BLOCK') {
        e.stopPropagation();
        e.preventDefault();
        mode = null;
        return;
      }

      /* UNDECIDED (tap — no significant movement) */
      if (mode === null && !g.decided) {
        const t = e.changedTouches?.[0];
        if (!t) { mode = null; return; }
        const dist = Math.hypot(t.clientX - g.startX, t.clientY - g.startY);
        const dur = Date.now() - g.startTime;

        /* Helper: cancel any library state from un-blocked touchstart */
        const cancelLib = () => {
          try {
            const pf = flipBookRef.current?.pageFlip();
            if (pf) { if (pf.ui) pf.ui.touchPoint = null; pf.userStop({ x: 0, y: 0 }, true); }
          } catch (_) {}
        };

        /* Image tap → open lightbox */
        if (dist < 20 && g.imgSrc) {
          e.stopPropagation();
          e.preventDefault();
          setLightboxSrc(g.imgSrc);
          cancelLib();
          mode = null;
          return;
        }

        /* Double-tap zoom (non-image areas) */
        if (dist < 15 && dur < 300) {
          const now = Date.now();
          if (now - g.lastTapTime < 400) {
            e.stopPropagation();
            e.preventDefault();
            if (zoomRef.current > 1) {
              zoomRef.current = 1; setZoomLevel(1);
              panRef.current = { x: 0, y: 0 }; setPanPos({ x: 0, y: 0 });
            } else {
              zoomRef.current = 2; setZoomLevel(2);
            }
            cancelLib();
            g.lastTapTime = 0;
            mode = null;
            return;
          }
          g.lastTapTime = now;
        }

        /* Block ALL remaining taps + cancel library state */
        e.stopPropagation();
        e.preventDefault();
        cancelLib();
      }

      mode = null;
    };

    const onTC = () => {
      cancelMomentum();
      mode = null;
      try { const pf = flipBookRef.current?.pageFlip(); if (pf) pf.userStop({ x: 0, y: 0 }, true); } catch (_) {}
    };

    el.addEventListener('touchstart', onTS, { capture: true, passive: false });
    el.addEventListener('touchmove', onTM, { capture: true, passive: false });
    el.addEventListener('touchend', onTE, { capture: true, passive: false });
    el.addEventListener('touchcancel', onTC, { capture: true, passive: true });
    return () => {
      cancelMomentum();
      el.removeEventListener('touchstart', onTS, { capture: true });
      el.removeEventListener('touchmove', onTM, { capture: true });
      el.removeEventListener('touchend', onTE, { capture: true });
      el.removeEventListener('touchcancel', onTC, { capture: true });
    };
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = useMemo(() => articleTranslations[articleLang] || articleTranslations['en'], [articleLang]);
  const ft = useMemo(() => ferrari599Translations[articleLang] || ferrari599Translations['en'], [articleLang]);

  const isFerrari599 = slug === 'the-30-who-were-right';
  const total = isFerrari599 ? 18 : 15;

  const articleTitle = isFerrari599 ? ft.hero_title : t.hero?.title;
  const articleHeroImage = isFerrari599 ? article?.images?.cover : article?.images?.hero;
  useDocumentMeta({
    title: articleTitle ? `${articleTitle} | Revs Tales | Revs Vibes` : 'Revs Tales | Revs Vibes',
    description: articleTitle ? `Read ${articleTitle} in Revs Tales, the journal of Revs Vibes.` : 'Read Revs Tales, the journal of Revs Vibes.',
    canonical: `https://www.revsvibes.com/tales/${slug}`,
    image: articleHeroImage,
  });

  /* Hide platform watermark on article pages */
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.setProperty('display', 'none', 'important');
    return () => {
      if (badge) badge.style.setProperty('display', 'inline-flex', 'important');
    };
  }, []);

  const go = useCallback((target, direction) => {
    if (anim || target < 0 || target >= total || target === cur) return;
    setDir(direction);
    setNext(target);
    setAnim('turning');
    setTimeout(() => {
      setCur(target);
      setNext(null);
      setAnim('');
    }, isMobile ? 650 : 420);
  }, [anim, cur, total, isMobile]);

  const fwd = useCallback(() => go(cur + 1, 1), [cur, go]);
  const bck = useCallback(() => go(cur - 1, -1), [cur, go]);

  useEffect(() => {
    const h = (e) => {
      if (lightboxSrc) return;
      if (isMobile) {
        if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); flipBookRef.current?.curlNext(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); flipBookRef.current?.curlPrev(); }
      } else {
        if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); fwd(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); bck(); }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [fwd, bck, lightboxSrc, isMobile]);

  const onTS = useCallback((e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const onTE = useCallback((e) => {
    if (!touchRef.current || lightboxSrc) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    const dy = e.changedTouches[0].clientY - touchRef.current.y;
    // Only flip if swipe is clearly horizontal (not vertical scrolling)
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 2) {
      dx < 0 ? fwd() : bck();
    }
    touchRef.current = null;
  }, [fwd, bck, lightboxSrc]);

  const openLightbox = useCallback((src) => setLightboxSrc(src), []);
  const closeLightbox = useCallback(() => setLightboxSrc(null), []);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="article-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-light text-gray-900 mb-4">Article not found</h1>
          <Link to="/blog" className="text-sm text-gray-500 hover:text-black">{t.backLink}</Link>
        </div>
      </div>
    );
  }

  const img = article.images;

  /* F599 adapters: map Ferrari image keys → 935 component slot names */
  const fi = isFerrari599 ? {
    hero: img.cover,
    detail: img.openingReveal,
    closing: img.ending,
  } : img;

  /* F599 text adapter: map Ferrari translation keys → 935 component text structure */
  const ftt = isFerrari599 ? {
    hero: { title: ft.hero_title, headline: ft.hero_headline, subheadline: ft.hero_tagline },
    opening: ft.ch1_silence,
    theBeginning: { title: ft.chapter_titles.ch2, beforeImage: ft.ch2_refused, afterImage: [] },
    aCondition: { title: ft.chapter_titles.ch3, paragraphs: ft.ch3_lost },
    theProject: ft.book_closed,
    shapeOfIntent: { title: ft.chapter_titles.ch4, paragraphs: ft.ch4_waited },
    rewritingMachine: { title: ft.chapter_titles.ch5, beforeImage: ft.interior_text, afterImage: [] },
    backLink: t.backLink,
  } : t;

  const pages = isFerrari599 ? [
    /* 1  Cover */
    <P1 t={ftt} img={fi} onOpen={openLightbox} />,
    /* 2  Opening — side profile + ch1 */
    <F599P2Opening t={ftt} img={fi} onOpen={openLightbox} />,
    /* 3  Text — ch2 */
    <F599P3Text t={ftt} />,
    /* 4  Full bleed — front presence */
    <F599FullBleed imgSrc={img.presence} onOpen={openLightbox} mobile={isMobile} testId="page-presence-bleed" />,
    /* 5  Text — ch3 + book_closed */
    <PCondition t={ftt} />,
    /* 6  Full bleed — rear 3/4 */
    <F599FullBleed imgSrc={img.sideProfile} onOpen={openLightbox} mobile={isMobile} testId="page-rear34-bleed" />,
    /* 7  Text — ch4 */
    <P5 t={ftt} />,
    /* 8  SHIFTER — full bleed, dominant */
    <F599FullBleed imgSrc={img.tactile} onOpen={openLightbox} mobile={isMobile} testId="page-shifter-bleed" bg="#0a0a0a" />,
    /* 9  Rim — ch5 */
    <PRollcage img={{rollcage: img.wheel}} onOpen={openLightbox} textParagraphs={Array.isArray(ft.interior_text) ? ft.interior_text : [ft.interior_text]} title={ft.chapter_titles.ch5} imgFit="contain" imgPosition="center center" />,
    /* 10 Interior / return text — cabin + ch6 */
    <PRollcage img={{rollcage: img.cabin}} onOpen={openLightbox} textParagraphs={ft.cockpit_text} title={ft.chapter_titles.ch6} />,
    /* 11 Ferrari badge — full spread */
    <F599FullBleed imgSrc="/articles/ferrari-599/15_badge_hires.png" onOpen={openLightbox} mobile={isMobile} testId="page-badge-spread" imgPosition="center 35%" imgFit={isMobile ? 'cover' : undefined} />,
    /* 12 Seats */
    <F599PlaqueSingle textParagraphs={ft.seats_text} imgSrc={img.seats} onOpen={openLightbox} />,
    /* 12 UNDERBODY — full bleed */
    <F599FullBleed imgSrc={img.conversion} onOpen={openLightbox} mobile={isMobile} testId="page-underbody-bleed" bg="#1a1a18" />,
    /* 13 What It Became — invoice + text */
    <PRollcage img={{rollcage: '/articles/ferrari-599/14_invoice.png'}} onOpen={openLightbox} textParagraphs={[...ft.return_gate.slice(0,1), ...ft.return_gate_dramatic, ...ft.return_gate.slice(1), ...ft.what_it_became]} title={ft.chapter_titles.ch7} imgFit="contain" imgPosition="center top" />,
    /* 15 Final text */
    <F599FinalText
      sections={[
        { title: ft.chapter_titles.ch8, paragraphs: ft.the_30 },
      ]}
    />,
    /* 16 Closing statement */
    <F599ClosingStatement
      bodyParagraphs={ft.closing_body}
      emphasisLines={ft.closing_emphasis}
      taglineLines={ft.closing_tagline}
      credits={ft.credits}
    />,
    /* 17 Final image — full width */
    <F599FullBleed imgSrc={img.ending} onOpen={openLightbox} mobile={isMobile} testId="page-final-image-bleed" bg="#1a1a18" />,
    /* 18 Brand page */
    <P11 t={ftt} logoSrc={img.endPage} />,
  ] : [
    <P1 t={t} img={img} onOpen={openLightbox} />,
    <P2 t={t} img={img} onOpen={openLightbox} />,
    <P3 t={t} img={img} onOpen={openLightbox} />,
    <PCondition t={t} />,
    <P4 img={img} onOpen={openLightbox} mobile={isMobile} />,
    <P5 t={t} />,
    <P6 t={t} img={img} onOpen={openLightbox} />,
    <P7 t={t} img={img} onOpen={openLightbox} />,
    <P8 img={img} onOpen={openLightbox} />,
    <PPlaque img={img} onOpen={openLightbox} />,
    <PRollcage img={img} onOpen={openLightbox} />,
    <P9 t={t} />,
    <PClosingStatement />,
    <P10 img={img} onOpen={openLightbox} />,
    <P11 t={t} />,
  ];

  const origin = dir === 1 ? 'left center' : 'right center';
  const curl = dir === 1 ? 'rotateY(-105deg)' : 'rotateY(105deg)';
  const shadow = dir === 1
    ? 'linear-gradient(to right, rgba(0,0,0,0.12) 0%, transparent 35%)'
    : 'linear-gradient(to left, rgba(0,0,0,0.12) 0%, transparent 35%)';
  const edgeShadow = dir === 1
    ? '6px 0 28px rgba(0,0,0,0.15)' : '-6px 0 28px rgba(0,0,0,0.15)';

  /* ═══════════════════════════════════════════
     MOBILE / TABLET — CATALOGUE VIEWER ENGINE
     Strict isolated viewer. No webpage behavior.
     ═══════════════════════════════════════════ */
  if (isMobile && flipDims) {
    const { flipW, flipH, contentScale, isLandscape } = flipDims;
    contentScaleRef.current = contentScale;
    const isPortraitImmersive = immersive && !isLandscape;

    const zoomBtnStyle = {
      width: 36, height: 36,
      borderRadius: 8,
      border: 'none',
      background: 'rgba(255,255,255,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
    };

    const controlsVisible = isLandscape && !immersive;
    const controlPillClearance = controlsVisible ? 72 : 0;
    const topUIClearance = controlsVisible ? 48 : 0;

    return (
      <div
        ref={flipContainerRef}
        style={{
          position: 'fixed', inset: 0,
          overflow: 'hidden',
          background: 'radial-gradient(ellipse at center, #111 0%, #0a0a0a 65%, #060606 100%)',
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: controlPillClearance,
          paddingTop: topUIClearance,
          boxSizing: 'border-box',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        data-testid="article-page"
      >

        {/* Visual enhancements — CSS only, zero behavior changes */}
        <style>{`
          /* Arrow active state — full opacity on press */
          [data-testid="page-prev-zone"]:active,
          [data-testid="page-next-zone"]:active {
            opacity: 1 !important;
            transform: translateY(-50%) scale(0.94) !important;
          }
          ${isFerrari599 ? `
          [data-testid="article-hero-image"] { object-position: 40% center !important; }
          [data-testid="article-subheadline"] { color: #fff !important; }
          ` : ''}
        `}</style>

        {/* Zoom + pan container — GPU-accelerated */}
        <div style={{
          transform: zoomLevel > 1
            ? `translate3d(${panPos.x}px, ${panPos.y}px, 0) scale(${zoomLevel})`
            : 'translate3d(0,0,0)',
          transformOrigin: 'center center',
          willChange: 'transform',
        }}>
          <BookEngine
            key={articleLang}
            ref={flipBookRef}
            width={flipW}
            height={flipH}
            contentScale={contentScale}
            pages={pages}
            onFlip={(pageIndex) => setCur(pageIndex)}
          />
        </div>

        {/* ── VIEWER UI OVERLAYS ── */}

        {/* Zoom toolbar — hidden in immersive */}
        {!immersive && (
        <div
          data-testid="zoom-toolbar"
          style={{
            position: 'fixed', bottom: 16, left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex', gap: 6, alignItems: 'center',
            background: 'rgba(0,0,0,0.5)',
            borderRadius: 10,
            padding: '4px 6px',
            zIndex: 60,
          }}
        >
          <button
            onClick={() => { const z = Math.max(1, zoomRef.current - 0.5); zoomRef.current = z; setZoomLevel(z); if (z <= 1) { panRef.current = { x: 0, y: 0 }; setPanPos({ x: 0, y: 0 }); } }}
            style={zoomBtnStyle}
            aria-label="Zoom out"
            data-testid="zoom-out-btn"
          >
            <ZoomOut size={16} color="rgba(255,255,255,0.7)" strokeWidth={1.5} />
          </button>
          <span
            data-testid="zoom-indicator"
            style={{
              fontSize: 11, fontWeight: 500,
              color: 'rgba(255,255,255,0.6)',
              minWidth: 36, textAlign: 'center',
              letterSpacing: '0.04em',
            }}
          >
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            onClick={() => { const z = Math.min(3, zoomRef.current + 0.5); zoomRef.current = z; setZoomLevel(z); }}
            style={zoomBtnStyle}
            aria-label="Zoom in"
            data-testid="zoom-in-btn"
          >
            <ZoomIn size={16} color="rgba(255,255,255,0.7)" strokeWidth={1.5} />
          </button>
          {zoomLevel > 1 && (
            <button
              onClick={() => { zoomRef.current = 1; setZoomLevel(1); panRef.current = { x: 0, y: 0 }; setPanPos({ x: 0, y: 0 }); }}
              style={zoomBtnStyle}
              aria-label="Reset zoom"
              data-testid="zoom-reset-btn"
            >
              <Minimize2 size={14} color="rgba(255,255,255,0.7)" strokeWidth={1.5} />
            </button>
          )}
        </div>
        )}

        {!immersive && cur === 0 && zoomLevel <= 1 && <LanguageSelector lang={articleLang} onChangeLang={setArticleLang} />}

        {/* Nav arrow — previous */}
        {zoomLevel <= 1 && cur > 0 && (
          <button
            onClick={() => flipBookRef.current?.curlPrev()}
            style={{
              position: 'fixed', left: isPortraitImmersive ? 6 : 12, top: '50%',
              transform: 'translateY(-50%)',
              width: isPortraitImmersive ? 32 : (immersive ? 38 : 48),
              height: isPortraitImmersive ? 32 : (immersive ? 38 : 48),
              borderRadius: '50%',
              background: isPortraitImmersive ? 'rgba(0,0,0,0.15)' : (immersive ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.45)'),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: isPortraitImmersive ? 'none' : '1px solid rgba(255,255,255,0.06)',
              boxShadow: isPortraitImmersive ? 'none' : '0 2px 12px rgba(0,0,0,0.3)',
              zIndex: 50,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transition: 'opacity 0.4s ease, transform 0.15s ease, background 0.2s ease',
              opacity: isPortraitImmersive ? (arrowsRevealed ? 0.85 : 0.4) : (immersive ? 0.5 : 0.7),
            }}
            aria-label="Previous page"
            data-testid="page-prev-zone"
          >
            <ChevronLeft size={isPortraitImmersive ? 14 : (immersive ? 16 : 22)} strokeWidth={2} color="#fff" />
          </button>
        )}

        {/* Nav arrow — next */}
        {zoomLevel <= 1 && cur < total - 1 && (
          <button
            onClick={() => flipBookRef.current?.curlNext()}
            style={{
              position: 'fixed', right: isPortraitImmersive ? 6 : 12, top: '50%',
              transform: 'translateY(-50%)',
              width: isPortraitImmersive ? 32 : (immersive ? 38 : 48),
              height: isPortraitImmersive ? 32 : (immersive ? 38 : 48),
              borderRadius: '50%',
              background: isPortraitImmersive ? 'rgba(0,0,0,0.15)' : (immersive ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.45)'),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: isPortraitImmersive ? 'none' : '1px solid rgba(255,255,255,0.06)',
              boxShadow: isPortraitImmersive ? 'none' : '0 2px 12px rgba(0,0,0,0.3)',
              zIndex: 50,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transition: 'opacity 0.4s ease, transform 0.15s ease, background 0.2s ease',
              opacity: isPortraitImmersive ? (arrowsRevealed ? 0.85 : 0.4) : (immersive ? 0.5 : 0.7),
            }}
            aria-label="Next page"
            data-testid="page-next-zone"
          >
            <ChevronRight size={isPortraitImmersive ? 14 : (immersive ? 16 : 22)} strokeWidth={2} color="#fff" />
          </button>
        )}

        {/* Page counter — ALWAYS visible: viewer overlay layer, above all page/fold/shadow layers */}
        <div
          style={{
            position: 'fixed', bottom: 54, right: 18,
            zIndex: 70, pointerEvents: 'none', userSelect: 'none',
          }}
          data-testid="page-counter"
        >
          <span style={{
            fontSize: 11, fontWeight: 500, letterSpacing: '0.15em',
            color: 'rgba(255,255,255,0.5)',
          }}>
            {cur + 1} / {total}
          </span>
        </div>

        {/* Landscape view mode toggle — hidden in immersive */}
        {!immersive && isLandscape && zoomLevel <= 1 && (
          <button
            onClick={() => setLandscapeMode(m => m === 'full' ? 'framed' : 'full')}
            style={{
              position: 'fixed', top: 12, right: 72,
              width: 30, height: 30, borderRadius: 8,
              background: 'rgba(255,255,255,0.08)',
              border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', zIndex: 50,
              WebkitTapHighlightColor: 'transparent',
            }}
            aria-label={landscapeMode === 'full' ? 'Switch to framed view' : 'Switch to full view'}
            data-testid="landscape-mode-toggle"
          >
            {landscapeMode === 'full'
              ? <Minimize2 size={13} color="rgba(255,255,255,0.5)" strokeWidth={1.5} />
              : <Maximize2 size={13} color="rgba(255,255,255,0.5)" strokeWidth={1.5} />
            }
          </button>
        )}

        {/* Back link — hidden in immersive */}
        {!immersive && cur === total - 1 && zoomLevel <= 1 && (
          <Link
            to="/blog"
            style={{
              position: 'fixed', top: 14, left: 18,
              fontSize: 11, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)',
              zIndex: 50, textDecoration: 'none',
              display: 'flex', alignItems: 'center',
            }}
            data-testid="article-back-link-overlay"
          >
            <ChevronLeft size={12} style={{ marginRight: 4 }} />
            {t.backLink}
          </Link>
        )}

        {/* Enter immersive mode — hidden when already immersive or zoomed */}
        {!immersive && zoomLevel <= 1 && (
          <button
            onClick={enterImmersive}
            style={{
              position: 'fixed', bottom: 16, right: 16,
              height: 32, borderRadius: 8,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '0 12px',
              cursor: 'pointer', zIndex: 60,
              WebkitTapHighlightColor: 'transparent',
            }}
            data-testid="enter-immersive-btn"
            aria-label="Enter full view"
          >
            <Maximize2 size={12} color="rgba(255,255,255,0.45)" strokeWidth={1.5} />
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
              Full View
            </span>
          </button>
        )}

        {/* Exit immersive mode */}
        {immersive && (
          <button
            onClick={exitImmersive}
            style={{
              position: 'fixed', top: 10, right: 10,
              width: 28, height: 28, borderRadius: 7,
              background: 'rgba(0,0,0,0.25)',
              border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', zIndex: 60,
              WebkitTapHighlightColor: 'transparent',
            }}
            data-testid="exit-immersive-btn"
            aria-label="Exit full view"
          >
            <X size={13} color="rgba(255,255,255,0.4)" strokeWidth={1.5} />
          </button>
        )}

        {lightboxSrc && <MobileLightbox src={lightboxSrc} onClose={closeLightbox} />}
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     DESKTOP — FLIPBOOK
     ═══════════════════════════════════════════ */
  return (
    <div
      className={`fixed inset-0 overflow-hidden${isFerrari599 ? ' f599-article' : ''}`}
      style={{ zIndex: 40, background: '#F4F3EF', perspective: 2200 }}
      onTouchStart={onTS}
      onTouchEnd={onTE}
      data-testid="article-page"
    >
      {isFerrari599 && <style>{`
        .f599-article [data-testid="article-hero-image"] { object-position: 40% center !important; }
        .f599-article [data-testid="article-subheadline"] { color: #fff !important; }
      `}</style>}
      <GrainFilter />
      {cur === 0 && <LanguageSelector lang={articleLang} onChangeLang={setArticleLang} />}

      {/* Incoming page (underneath) */}
      {next !== null && (
        <div className="absolute inset-0" style={{ zIndex: 1 }}>
          {pages[next]}
          {anim && <div className="absolute inset-0 pointer-events-none" style={{ background: shadow }} />}
        </div>
      )}

      {/* Current page (curls away) */}
      <div
        ref={pageRef}
        className="absolute inset-0"
        style={{
          transformOrigin: origin,
          transform: anim ? curl : 'rotateY(0)',
          opacity: anim ? 0 : 1,
          transition: anim
            ? 'transform 420ms cubic-bezier(0.4,0,0.2,1), opacity 320ms ease 100ms'
            : 'none',
          zIndex: anim ? 3 : 2,
          backfaceVisibility: 'hidden',
          boxShadow: anim ? edgeShadow : 'none',
        }}
      >
        {pages[cur]}
      </div>

      {/* LEFT ARROW — always visible, white, glow */}
      {cur > 0 && (
        <button
          onClick={bck}
          onMouseEnter={() => setHover('L')}
          onMouseLeave={() => setHover(null)}
          className="absolute left-0 top-0 h-full flex items-center justify-center"
          style={{ width: '18vw', zIndex: 20, cursor: 'w-resize', background: 'transparent' }}
          aria-label="Previous page"
          data-testid="page-prev-zone"
        >
          <div
            style={{
              position: 'fixed', left: 24, top: '50%',
              transform: hover === 'L' ? 'translateY(-50%) scale(1.05)' : 'translateY(-50%)',
              width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '50%',
              background: hover === 'L' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              opacity: hover === 'L' ? 1 : 0.75,
              transition: 'opacity 0.2s ease, background 0.2s ease, transform 0.2s ease',
            }}
          >
            <ChevronLeft size={24} strokeWidth={2} color="#fff" style={{ opacity: 0.9 }} />
          </div>
        </button>
      )}

      {/* RIGHT ARROW — always visible, white, glow */}
      {cur < total - 1 && (
        <button
          onClick={fwd}
          onMouseEnter={() => setHover('R')}
          onMouseLeave={() => setHover(null)}
          className="absolute right-0 top-0 h-full flex items-center justify-center"
          style={{ width: '18vw', zIndex: 20, cursor: 'e-resize', background: 'transparent' }}
          aria-label="Next page"
          data-testid="page-next-zone"
        >
          <div
            style={{
              position: 'fixed', right: 24, top: '50%',
              transform: hover === 'R' ? 'translateY(-50%) scale(1.05)' : 'translateY(-50%)',
              width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '50%',
              background: hover === 'R' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              opacity: hover === 'R' ? 1 : 0.75,
              transition: 'opacity 0.2s ease, background 0.2s ease, transform 0.2s ease',
            }}
          >
            <ChevronRight size={24} strokeWidth={2} color="#fff" style={{ opacity: 0.9 }} />
          </div>
        </button>
      )}

      {/* PAGE COUNTER — bottom-right, inward from edge */}
      {(() => {
        const darkPages = isFerrari599
          ? [0, 3, 5, 7, 10, 12, 16, 17]
          : [0, 1, 4, 6, 8, 13, 14];
        const isDark = darkPages.includes(cur);
        return (
          <div
            className="fixed pointer-events-none select-none"
            style={{ bottom: 32, right: 48, zIndex: 20 }}
            data-testid="page-counter"
          >
            <span style={{
              position: 'relative',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.15em',
              color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(30,30,30,0.5)',
              textShadow: isDark ? '0 1px 6px rgba(0,0,0,0.5)' : 'none',
            }}>
              {cur + 1} / {total}
            </span>
          </div>
        );
      })()}

      {/* BACK TO REVS TALES — last page only, above arrow zones */}
      {cur === total - 1 && (
        <Link
          to="/blog"
          className="flex items-center transition-colors"
          style={{
            position: 'fixed', bottom: 32, left: '5vw',
            fontSize: 11, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)',
            zIndex: 25, textDecoration: 'none',
          }}
          data-testid="article-back-link-overlay"
        >
          <ChevronLeft size={12} className="mr-1" />
          {t.backLink}
        </Link>
      )}

      {/* LIGHTBOX */}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={closeLightbox} />}
    </div>
  );
};

export default ArticlePage;
